import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import ChargebeeCheckoutModal from 'src/component-modal/ChargebeeCheckoutModal/ChargebeeCheckoutModal'
import { useEnvConfig } from 'src/context/ConfigContext/useConfig'
import { SaasUrlResponse } from 'src/models/organizations.models'
import {
  usePostCreateSubscription,
  usePutReactivateSubscription,
} from 'src/queries/organizations/saas.queries'
import { orgQueryKeys } from 'src/queries/queries.constants'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'

export const CHARGEBEE_CHECKOUT_EVENT = 'chargebee embedded checkout'

export type ChargebeeFlow = 'create' | 'reactivate'
export type ChargebeeEntryPoint = 'pricing' | 'upgrade'

export type TriggerOpts = {
  flow: ChargebeeFlow
  entryPoint: ChargebeeEntryPoint
  dismissible: boolean
  onSuccess: (hostedPageId: string) => void
  /** Fired when the embedded checkout modal closes (success OR dismiss). */
  onClose?: () => void
}

// `redirect` is set internally by the hook, so callers don't pass it.
type CreateArgs = Omit<
  Parameters<ReturnType<typeof usePostCreateSubscription>['mutate']>[0],
  'redirect'
>
type ReactivateArgs = Omit<
  Parameters<ReturnType<typeof usePutReactivateSubscription>['mutate']>[0],
  'redirect'
>

export type MutationArgs =
  | { type: 'create'; args: CreateArgs }
  | { type: 'reactivate'; args: ReactivateArgs }

type Session = {
  url: string
  opts: TriggerOpts
  // Optional — only set when the session came from a mutation flow that
  // can regenerate a fresh hosted-page URL on non-dismissible close.
  // For pre-baked URL sessions (e.g. dynamic pricing CTA), there's no
  // mutation to re-run; close-refetch reuses the same URL. Only safe
  // when the session is dismissible since reused URLs may be stale.
  reopenMutation?: MutationArgs
}

export default function useChargebeeCheckout(): {
  trigger: (args: MutationArgs, opts: TriggerOpts) => void
  /** Open the checkout modal with a pre-existing hosted-page URL. */
  openCheckoutUrl: (url: string, opts: TriggerOpts) => void
  renderCheckout: () => React.ReactElement | null
} {
  const { analytics } = useAnalyticsContext()
  const { CHARGEBEE_SITE } = useEnvConfig()
  const queryClient = useQueryClient()
  const { enable_embedded_chargebee_checkout } = useSelector(
    selectFeatureFlaggerFromState,
  )

  const { mutate: createMutate } = usePostCreateSubscription()
  const { mutate: reactivateMutate } = usePutReactivateSubscription()

  const [session, setSession] = useState<Session | null>(null)

  const runMutation = useCallback(
    (
      mutationArgs: MutationArgs,
      redirect: boolean,
      mutOpts: { onSuccess: (r: SaasUrlResponse) => void },
    ): void => {
      if (mutationArgs.type === 'create') {
        createMutate({ ...mutationArgs.args, redirect }, mutOpts)
      } else {
        reactivateMutate({ ...mutationArgs.args, redirect }, mutOpts)
      }
    },
    [createMutate, reactivateMutate],
  )

  /**
   * Returns true if the embedded modal path should be used. When false,
   * the caller must fall back to redirecting. Fires the
   * `fallback_redirect` analytics event as a side-effect on the false
   * branch so each entry point has consistent telemetry.
   */
  const shouldEmbedOrTrackFallback = useCallback(
    (opts: TriggerOpts): boolean => {
      const configured = Boolean(CHARGEBEE_SITE)
      const shouldEmbed = enable_embedded_chargebee_checkout && configured
      if (!shouldEmbed) {
        analytics.track(CHARGEBEE_CHECKOUT_EVENT, {
          action: 'fallback_redirect',
          flow: opts.flow,
          entry_point: opts.entryPoint,
          reason: !enable_embedded_chargebee_checkout
            ? 'flag_off'
            : 'env_unconfigured',
        })
      }
      return shouldEmbed
    },
    [analytics, CHARGEBEE_SITE, enable_embedded_chargebee_checkout],
  )

  const trackOpened = useCallback(
    (opts: TriggerOpts): void => {
      analytics.track(CHARGEBEE_CHECKOUT_EVENT, {
        action: 'opened',
        flow: opts.flow,
        entry_point: opts.entryPoint,
      })
    },
    [analytics],
  )

  const openCheckoutUrl = useCallback(
    (url: string, opts: TriggerOpts): void => {
      if (!shouldEmbedOrTrackFallback(opts)) {
        window.location.href = url
        return
      }
      setSession({ url, opts })
      trackOpened(opts)
    },
    [shouldEmbedOrTrackFallback, trackOpened],
  )

  const trigger = useCallback(
    (mutationArgs: MutationArgs, opts: TriggerOpts): void => {
      if (!shouldEmbedOrTrackFallback(opts)) {
        // Let the mutation's own onSuccess do window.location.href.
        runMutation(mutationArgs, true, { onSuccess: (): void => undefined })
        return
      }

      runMutation(mutationArgs, false, {
        onSuccess: (r: SaasUrlResponse): void => {
          if (r.url === null) {
            analytics.track(CHARGEBEE_CHECKOUT_EVENT, {
              action: 'errored',
              flow: opts.flow,
              entry_point: opts.entryPoint,
              error_type: 'null_url',
            })
            return
          }
          setSession({ url: r.url, opts, reopenMutation: mutationArgs })
          trackOpened(opts)
        },
      })
    },
    [analytics, runMutation, shouldEmbedOrTrackFallback, trackOpened],
  )

  const renderCheckout = useCallback((): React.ReactElement | null => {
    if (!session || !CHARGEBEE_SITE) return null

    return (
      <ChargebeeCheckoutModal
        isOpen
        url={session.url}
        site={CHARGEBEE_SITE}
        dismissible={session.opts.dismissible}
        flow={session.opts.flow}
        entryPoint={session.opts.entryPoint}
        onSuccess={(id): void => {
          queryClient.invalidateQueries({ queryKey: orgQueryKeys.all })
          session.opts.onSuccess(id)
          setSession(null)
        }}
        onClose={(): void => {
          session.opts.onClose?.()
          setSession(null)
        }}
        onRefreshUrl={(cb): void => {
          if (!session.reopenMutation) {
            // No mutation to re-run; reuse the same URL. Only safe for
            // dismissible sessions since a reused URL may be stale.
            cb(session.url)
            return
          }
          runMutation(session.reopenMutation, false, {
            onSuccess: (r: SaasUrlResponse): void => {
              if (r.url !== null) cb(r.url)
            },
          })
        }}
      />
    )
  }, [CHARGEBEE_SITE, queryClient, runMutation, session])

  return { trigger, openCheckoutUrl, renderCheckout }
}
